import { useState, useEffect } from "react";
import { useAuthStore } from "@/store/authStore";

export const LoginPage: React.FC = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const { login, isLoading, error, clearError } = useAuthStore();

  useEffect(() => {
    // Clear any previous errors when component mounts
    clearError();
  }, [clearError]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (username.trim() && password.trim()) {
      await login(username.trim(), password);
    }
  };

  return (
    <div className="min-h-screen bg-custom-light-50 flex flex-col">
      {/* Custom Navy Header */}
      <header className="bg-custom-navy border-b border-custom-dark px-4 sm:px-6 lg:px-8 shadow-md">
        <div className="max-w-7xl mx-auto flex justify-between items-center h-16">
          <div className="flex items-center">
            <h1 className="text-lg font-bold text-white">HeroCon.Ai</h1>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="max-w-md w-full">
          {/* Login Form Card */}
          <div className="bg-white rounded-xl shadow-lg p-8 border border-custom-light-200">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-semibold text-custom-navy mb-2">
                Welcome Back
              </h2>
              <p className="text-custom-grey">Code Compliance Hub</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Username Field */}
              <div>
                <label
                  htmlFor="username"
                  className="block text-sm font-medium text-custom-navy mb-2"
                >
                  Username
                </label>
                <input
                  id="username"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Enter your username"
                  className="w-full px-4 py-3 bg-custom-light-50 border border-custom-light-200 rounded-lg focus:ring-2 focus:ring-custom-navy focus:border-custom-navy transition-all duration-200 text-custom-navy placeholder-custom-grey"
                  autoFocus
                  required
                  disabled={isLoading}
                />
              </div>

              {/* Password Field */}
              <div>
                <label
                  htmlFor="password"
                  className="block text-sm font-medium text-custom-navy mb-2"
                >
                  Password
                </label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="w-full px-4 py-3 bg-custom-light-50 border border-custom-light-200 rounded-lg focus:ring-2 focus:ring-custom-navy focus:border-custom-navy transition-all duration-200 text-custom-navy placeholder-custom-grey"
                  required
                  disabled={isLoading}
                />
              </div>

              {/* Error Message */}
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-red-600 text-sm">{error}</p>
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={!username.trim() || !password.trim() || isLoading}
                className="w-full bg-custom-navy text-white py-3 px-6 rounded-lg font-semibold hover:bg-custom-dark disabled:bg-gray-300 disabled:cursor-not-allowed transition-all duration-200 shadow-sm hover:shadow-md"
              >
                {isLoading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Signing in...
                  </div>
                ) : (
                  "Sign In"
                )}
              </button>
            </form>

            {/* Design Motto */}
            <div className="mt-6 text-center">
              <p className="text-lg font-semibold text-custom-navy">
                Design with confidence. Build without penalties.
              </p>
            </div>
          </div>

          {/* Footer */}
          <div className="text-center mt-6">
            <p className="text-custom-grey text-sm">
              Secure access to your code compliance workspace
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
